import type { QueryClient } from '@tanstack/react-query';
import {
  URL_ACCOUNTS,
  URL_CASH_MOVEMENTS,
  URL_CASH_MOVEMENT_ALLOCATION,
  URL_DOCUMENTS,
  URL_PAYMENT_SCHEDULES,
  URL_RECONCILIATION,
  URL_TENANTS,
} from '@/lib/constants/urls';
import { COMMESSA_QUERY_ROOT } from './query-key-root';

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

/** Una costante di URL entra nel pattern come testo, non come espressione. */
const literal = (segment: string) =>
  segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Scadenze documento: il client le chiama sia sulla collezione del tenant
 * (`/documents/payment-schedules[/:id]`) sia su un documento specifico
 * (`/documents/:documentId/payment-schedules`). Il segmento intermedio è
 * opzionale, ma `/payment-schedules` è obbligatorio: così `/documents` e
 * `/documents/:id` — le scritture sul documento — restano ESCLUSE, perché già
 * coperte dagli hook che chiamano `invalidateCommesseAfterDocumentWrite`.
 */
const PAYMENT_SCHEDULES_BRANCH = `${literal(URL_DOCUMENTS)}(?:/[^/?]+)?${literal(
  URL_PAYMENT_SCHEDULES
)}`;

/** I movimenti sono annidati sotto i conti: `/accounts/cash-movements`. */
const CASH_MOVEMENTS_BRANCH = `${literal(URL_ACCOUNTS)}${literal(
  URL_CASH_MOVEMENTS
)}`;

/**
 * URL dei domini finanziari che spostano l'incassato/pagato di una commessa.
 * Il pattern è composto dalle costanti di `lib/constants/urls`, le stesse che
 * costruiscono le richieste: un segmento scritto a mano che non esiste rende
 * l'invalidazione muta senza che nulla fallisca.
 */
const FINANCIAL_URL_PATTERN = new RegExp(
  `${literal(URL_TENANTS)}/([^/?]+)(?:${PAYMENT_SCHEDULES_BRANCH}|` +
    `${CASH_MOVEMENTS_BRANCH}|${literal(URL_CASH_MOVEMENT_ALLOCATION)}|` +
    `${literal(URL_RECONCILIATION)})(?:[/?]|$)`
);

/**
 * Invalida TUTTE le query commessa dopo una scrittura su un documento.
 *
 * L'invalidazione è incondizionata: il summary della commessa non dipende solo
 * dalle quote (`commessaAllocations`), ma anche da totali, stato, righe e
 * incassato del documento — qualunque scrittura può spostarlo. Il costo è
 * contenuto perché `invalidateQueries` rifetcha solo le query attive.
 */
export function invalidateCommesseAfterDocumentWrite(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: COMMESSA_QUERY_ROOT });
}

/**
 * Invalida le query commessa del tenant coinvolto dopo una scrittura
 * finanziaria (scadenze, movimenti, allocazioni, riconciliazione): cambiano
 * incassato e pagato, quindi il summary della commessa.
 *
 * Il tenantId compare in posizione 2 o 3 delle key commessa (dopo `list` /
 * `detail` / `summary`, e dopo `infinite` o `list-batches`), quindi il
 * predicato lo cerca ovunque nella key.
 */
export function invalidateCommesseAfterFinancialWrite(
  queryClient: QueryClient,
  request: { method?: string; url?: string }
) {
  if (!MUTATING_METHODS.includes(request.method?.toLowerCase() ?? '')) return;
  const match = request.url?.match(FINANCIAL_URL_PATTERN);
  if (!match) return;
  const tenantId = match[1];
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === COMMESSA_QUERY_ROOT[0] &&
      (query.queryKey as readonly unknown[]).includes(tenantId),
  });
}
